/**
 * London Smoke & Cure — monitoring pause / maintenance window helpers (pure).
 */

/**
 * @typedef {{
 *   id?: string,
 *   startMs: number,
 *   endMs?: number|null,
 *   reason?: string,
 *   kind?: string,
 * }} NjordMaintenanceWindow
 */

/**
 * @typedef {{
 *   paused?: boolean,
 *   pausedAtMs?: number|null,
 *   pausedUntilMs?: number|null,
 *   pausedBy?: string,
 *   pauseNote?: string,
 *   openWindowId?: string|null,
 * }} NjordMonitoringState
 */

/**
 * @param {NjordMonitoringState|null|undefined} monitoring
 * @param {number} [nowMs]
 */
export function njordMonitoringIsPaused(monitoring, nowMs = Date.now()) {
    if (!monitoring || typeof monitoring !== 'object') return false;
    if (!monitoring.paused) return false;
    const until = Number(monitoring.pausedUntilMs);
    if (Number.isFinite(until) && until > 0 && nowMs >= until) return false;
    return true;
}

/**
 * @param {number} tsMs
 * @param {NjordMaintenanceWindow[]} windows
 * @param {number} [nowMs]
 */
export function njordTsInMaintenanceWindow(tsMs, windows = [], nowMs = Date.now()) {
    const t = Number(tsMs);
    if (!Number.isFinite(t)) return false;
    for (const w of windows || []) {
        const start = Number(w?.startMs);
        if (!Number.isFinite(start)) continue;
        let end = w?.endMs == null || w?.endMs === '' ? null : Number(w.endMs);
        if (end == null || !Number.isFinite(end)) end = nowMs;
        if (t >= start && t <= end) return true;
    }
    return false;
}

/**
 * @param {number} tsMs
 * @param {Array<{ startMs: number, endMs: number, reason?: string }>} intervals
 * @returns {{ startMs: number, endMs: number, reason?: string }|null}
 */
export function findNjordMaintenanceIntervalAtMs(tsMs, intervals = []) {
    const t = Number(tsMs);
    if (!Number.isFinite(t)) return null;
    for (const w of intervals || []) {
        const start = Number(w?.startMs);
        const end = Number(w?.endMs);
        if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
        if (t >= start && t <= end) return w;
    }
    return null;
}

/**
 * Closed (or open→now) intervals for chart bands.
 * @param {NjordMaintenanceWindow[]} windows
 * @param {number} [nowMs]
 * @returns {Array<{ startMs: number, endMs: number, reason?: string }>}
 */
export function njordMaintenanceIntervalsMs(windows = [], nowMs = Date.now()) {
    const out = [];
    for (const w of windows || []) {
        const start = Number(w?.startMs);
        if (!Number.isFinite(start)) continue;
        let end = w?.endMs == null || w?.endMs === '' ? null : Number(w.endMs);
        if (end == null || !Number.isFinite(end)) end = nowMs;
        if (end <= start) continue;
        out.push({
            startMs: start,
            endMs: end,
            reason: w.reason ? String(w.reason) : 'Maintenance',
        });
    }
    return out.sort((a, b) => a.startMs - b.startMs);
}

/**
 * Tag readings that fall inside maintenance windows (ts is epoch seconds).
 * @param {Array<{ ts?: number }>} readings
 * @param {NjordMaintenanceWindow[]} windows
 * @param {number} [nowMs]
 */
export function annotateNjordReadingsWithMaintenance(readings = [], windows = [], nowMs = Date.now()) {
    if (!windows?.length) {
        return (readings || []).map((r) => ({ ...r, maintenance: false }));
    }
    return (readings || []).map((r) => {
        const tsSec = Number(r?.ts);
        const tsMs = Number.isFinite(tsSec) ? (tsSec > 1e12 ? tsSec : tsSec * 1000) : NaN;
        return {
            ...r,
            maintenance: njordTsInMaintenanceWindow(tsMs, windows, nowMs),
        };
    });
}

/**
 * Exclude maintenance-tagged readings from period stats (avg/min/max).
 * @param {Array<{ maintenance?: boolean }>} readings
 */
export function filterOutNjordMaintenanceReadings(readings = []) {
    return (readings || []).filter((r) => !r?.maintenance);
}
